/**
 * AdminAuditPage — Phase 13 P5 platform-admin Audit tab.
 *
 * Surfaces the Scene Corpus Audit framework as an interactive UI:
 *
 *   * "Run all audits" button → POST `/scenario-os/admin/audit/scene_corpus`
 *   * Per-audit "Run only this" buttons (filter via `?audits=`)
 *   * Severity badges + per-finding fix hints
 *   * Click-to-deep-link into the offending document via the
 *     existing scene / curriculum / question editor pages
 *
 * The page is gated to ``platform_admin`` callers — the route's auth
 * dependency rejects everyone else. The frontend simply hides this
 * page from non-admin sidebars.
 *
 * Why a stand-alone page (not a tab inside TenantAdminPage)
 * ---------------------------------------------------------
 *
 * Audit runs can take seconds; the page needs its own loading
 * indicator + finding-pivot table. Embedding inside the tenant
 * admin surface would crowd that page. The link from
 * TenantAdminPage's left rail keeps discoverability.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import CoachLayout from './CoachLayout';
import { useAuth } from '../../context/AuthContext';

const API_BASE =
  process.env.REACT_APP_SCENARIO_OS_API ||
  process.env.REACT_APP_API_BASE ||
  '/scenario-os';

const SEVERITY_TO_BADGE = {
  error:   { label: 'Error',   className: 'badge badge-error'   },
  warning: { label: 'Warning', className: 'badge badge-warning' },
  info:    { label: 'Info',    className: 'badge badge-info'    },
};

function deepLinkForFinding(finding) {
  // The audit's DocumentRef carries `collection` + `doc_id` + an
  // `extra` bag.  We emit the canonical designer route per
  // collection.  Unknown collections produce a copy-id chip; the
  // page falls back gracefully.
  const doc = finding.document;
  if (!doc) return null;
  const { collection, doc_id, extra = {} } = doc;
  const domain = extra.domain_id || extra.domain || '';
  switch (collection) {
    case 'scenes':
      return `/coach/scenes/${doc_id}?domain=${encodeURIComponent(domain)}`;
    case 'curriculum_modules':
    case 'content_curriculum_tracks':
      return `/coach/curriculum?domain=${encodeURIComponent(domain)}`;
    case 'question_templates':
      return `/coach/question_templates/${doc_id}?domain=${encodeURIComponent(domain)}`;
    default:
      return null;
  }
}

export default function AdminAuditPage() {
  const { learner } = useAuth();
  const [registeredAudits, setRegisteredAudits] = useState([]);
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError]   = useState(null);

  const fetchAuditList = useCallback(async () => {
    try {
      const token = localStorage.getItem('scenario_os_token');
      const resp = await fetch(`${API_BASE}/admin/audit/scene_corpus/audits`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setRegisteredAudits(Array.isArray(data.audits) ? data.audits : []);
    } catch (e) {
      setError(`Could not load audit list: ${e.message}`);
    }
  }, []);

  useEffect(() => { fetchAuditList(); }, [fetchAuditList]);

  const runAudit = useCallback(async (auditId) => {
    setRunning(true);
    setError(null);
    try {
      const url = new URL(`${API_BASE}/admin/audit/scene_corpus`, window.location.origin);
      if (auditId) url.searchParams.set('audits', auditId);
      const token = localStorage.getItem('scenario_os_token');
      const resp = await fetch(url.toString(), {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await resp.json();
      if (!resp.ok) {
        // 409 strict mode: the body is still an audit report.
        setReport(data.detail || data);
      } else {
        setReport(data);
      }
    } catch (e) {
      setError(`Audit run failed: ${e.message}`);
    } finally {
      setRunning(false);
    }
  }, []);

  const summary = report?.summary;

  return (
    <CoachLayout>
      <div className="admin-audit-page">
        <header className="admin-audit-header">
          <h1>Scene Corpus Audit</h1>
          <p className="muted">
            Validate platform invariants across scenes, curriculum,
            and question templates. Run after seed migrations or
            content imports.
          </p>
        </header>

        {error && (
          <div className="error-banner" role="alert">{error}</div>
        )}

        <section className="admin-audit-controls">
          <button
            type="button"
            className="btn-primary"
            disabled={running}
            onClick={() => runAudit(null)}
          >
            {running ? 'Running…' : 'Run all audits'}
          </button>
          <details className="admin-audit-individual">
            <summary>Run individual audit</summary>
            <ul>
              {registeredAudits.map((a) => (
                <li key={a.audit_id}>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={running}
                    onClick={() => runAudit(a.audit_id)}
                  >
                    {a.audit_id}
                  </button>
                  <span className="muted">{a.description}</span>
                </li>
              ))}
            </ul>
          </details>
        </section>

        {summary && (
          <section className="admin-audit-summary">
            <h2>Summary</h2>
            <ul className="admin-audit-counts">
              <li className={summary.errors ? 'is-error' : ''}>
                Errors: <strong>{summary.errors}</strong>
              </li>
              <li>Warnings: <strong>{summary.warnings}</strong></li>
              <li>Info: <strong>{summary.infos}</strong></li>
              <li>Status: <strong>{summary.ok ? 'OK' : 'NEEDS ATTENTION'}</strong></li>
            </ul>
            <span className="muted">
              Ran at {report.ran_at_iso} (schema v{report.schema_version})
            </span>
          </section>
        )}

        {report && (
          <section className="admin-audit-results">
            {report.audits.map((ar) => (
              <div className="admin-audit-card" key={ar.audit_id}>
                <h3>
                  {ar.audit_id}{' '}
                  <span className="muted">
                    ({ar.elapsed_ms} ms, errors {ar.counts.error},
                    warnings {ar.counts.warning})
                  </span>
                </h3>
                {ar.findings.length === 0 && (
                  <p className="ok-row">No findings — clean.</p>
                )}
                {ar.findings.length > 0 && (
                  <table className="admin-audit-findings">
                    <thead>
                      <tr>
                        <th>Severity</th>
                        <th>Rule</th>
                        <th>Message</th>
                        <th>Fix hint</th>
                        <th>Document</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ar.findings.map((f, i) => {
                        const badge = SEVERITY_TO_BADGE[f.severity] || SEVERITY_TO_BADGE.info;
                        const link = deepLinkForFinding(f);
                        return (
                          <tr key={`${ar.audit_id}-${i}`}>
                            <td>
                              <span className={badge.className}>{badge.label}</span>
                            </td>
                            <td><code>{f.rule}</code></td>
                            <td>{f.message}</td>
                            <td>{f.fix_hint || ''}</td>
                            <td>
                              {f.document && (
                                link
                                  ? <Link to={link}>{f.document.collection}/{f.document.doc_id}</Link>
                                  : <code>{f.document.collection}/{f.document.doc_id}</code>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </section>
        )}
      </div>
    </CoachLayout>
  );
}
